import React, { useState, useEffect, useCallback } from 'react'
import { Drawer, Steps, Button, message, Select, Typography, Popover } from 'antd'
import { useTranslation } from 'react-i18next'
import * as moment from 'moment'
import Moment from 'react-moment'
import { getFleetsByResource, createQuotasByResource, deleteQuotasByResource } from '../../resource-management.services'
import Calendar from './calendar.component'
import StepThree from './step-three.component'
import { useHistory } from 'react-router-dom'

const Capacity = (props) => {
  const { clientTimeZone, showDrawerCapacity, setShowDrawerCapacity, initialValues, formAction } = props
  const history = useHistory()
  const [state, setState] = useState(0)
  const [dates, setDates] = useState([])
  const [hours, setHours] = useState([])
  const [fleets, setFleets] = useState([])
  const [fleetId, setFleetId] = useState('')
  const [disableHours, setdisableHours] = useState(false)
  const [btnLoading, setBtnLoading] = useState(false)

  const { Step } = Steps
  const { Option } = Select
  const { Text } = Typography
  const { t } = useTranslation()

  const steps = [
    {
      title: '',
      content: fleets && (
        <>
          <Text strong>{t('resources.capacity.modal.text')}:</Text>
          <br />
          <Select
            style={fleetId === '' ? { color: '#868B90' } : {}}
            value={fleetId}
            className='resource-capacity-select-fleet'
            onChange={(id) => setFleetId(id)}
          >
            <Option key={-1} value={''}>
              {t('resources.capacity.modal.placeholder')}
            </Option>
            {((_) => {
              return fleets.map((fleet) => {
                return (
                  <Option key={fleet.id} value={fleet.id}>
                    {fleet.name}
                  </Option>
                )
              })
            })()}
          </Select>
        </>
      )
    },
    {
      title: '',
      content: (
        <>
          <Text strong>{t('resources.capacity.modal.text-date')}:</Text>
          <br />
          <Calendar dates={dates} setDates={setDates} showDrawerCapacity={showDrawerCapacity} />
        </>
      )
    },
    {
      title: '',
      content: <StepThree hours={hours} setHours={setHours} formAction={formAction} disableHours={disableHours} />
    }
  ]

  const splitQuotasByHour = (dates, hours, timeZone) => {
    moment.tz.setDefault(timeZone)
    const quotas = []
    for (let date of dates) {
      const originDate = moment(date).startOf('day').utc()
      for (let hourRange of hours) {
        const originStartHour = hourRange.start
        const originEndHour = hourRange.end
        for (let range = originStartHour; range <= originEndHour - 1; range++) {
          const from = originDate.clone().add(range, 'hours').format()
          const to = originDate
            .clone()
            .add(range + 1, 'hours')
            .format()
          quotas.push({ from, to })
        }
      }
    }
    return quotas
  }

  const alertMessage = ({ errors, numOfQuotas, numOfErrors, text }) => (
    <div className='alert-message-content'>
      <span>{`${numOfQuotas}/${numOfQuotas + numOfErrors} ${text}`}</span>
      <Popover
        placement='bottom'
        content={
          <div>
            {errors.map(({ resource, fleet, reason, from, to }, ix) => (
              <p key={ix}>
                <span className='dot' />
                <b>{` ${resource.firstName} ${resource.lastName}`}</b>
                {` ${t(`errors.${reason}`)} ${fleet.name} ${t('resources.capacity.in')} `}
                <Moment className='error-quota-date' format='lll' tz={clientTimeZone}>
                  {from}
                </Moment>
                {' - '}
                <Moment className='error-quota-date' format='LT' tz={clientTimeZone}>
                  {to}
                </Moment>
              </p>
            ))}
          </div>
        }
        trigger='click'
      >
        <Button type='link'>{t('resources.capacity.show-conflicts')}</Button>
      </Popover>
    </div>
  )

  const showMessage = (errors, quotas) => {
    if (!errors && !quotas) {
      message.success(t('resources.capacity.cleaned'))
      return
    }
    const numOfErrors = errors.length
    const numOfQuotas = quotas.length
    const text = `${t('resources.capacity.quotas-success')}`
    numOfErrors === 0 && message.success(`${numOfQuotas}/${numOfQuotas} ${text}`)
    numOfErrors > 0 &&
      message.warning(
        alertMessage({
          errors,
          numOfQuotas,
          numOfErrors,
          text
        }),
        20
      )
  }

  const done = async () => {
    try {
      setBtnLoading(true)
      const resourceId = initialValues.id
      const timeZoneFleet = fleets.find((fleet) => fleet.id === fleetId)
      const quotasByHour = splitQuotasByHour(dates, hours, timeZoneFleet?.timeZone || clientTimeZone)
      const result =
        formAction === 'add'
          ? await createQuotasByResource(fleetId, resourceId, quotasByHour)
          : await deleteQuotasByResource(fleetId, resourceId, quotasByHour)
      if (result) {
        const { errors, quotas } = result
        showMessage(errors, quotas)
        clearDataForm()
        setShowDrawerCapacity(false)
      }
      setBtnLoading(false)
    } catch (error) {
      setBtnLoading(false)
    }
  }

  const fleetsByResource = useCallback(async () => {
    const fleetsByResource = await getFleetsByResource(initialValues.id)
    if (fleetsByResource && fleetsByResource.fleets) {
      setFleets(fleetsByResource.fleets)
    }
  }, [initialValues.id])

  const clearDataForm = () => {
    setState(0)
    setDates([])
    setHours([])
    setFleets([])
    setFleetId('')
  }

  useEffect(() => {
    if (dates.length === 1 && moment().format('YYYY-MM-DD') === dates[0]) {
      setdisableHours(true)
    } else {
      setdisableHours(false)
    }
  }, [dates])

  useEffect(() => {
    if (initialValues.id && showDrawerCapacity) {
      fleetsByResource()
    }
    return () => {
      clearDataForm()
    }
  }, [fleetsByResource, initialValues, showDrawerCapacity])

  const handleShowDrawer = (_e) => {
    history.push(`/resources/users`)
    setShowDrawerCapacity(!showDrawerCapacity)
  }

  const isNextDisabled = (step) => {
    let disabled = true
    if (step === 0) disabled = !fleetId
    if (step === 1) disabled = !dates.length > 0
    if (step === 2) {
      if (hours.length > 0) {
        let errors = 0
        hours.forEach((hour) => {
          errors += typeof hour.start !== 'number' || typeof hour.end !== 'number' ? 1 : 0
        })
        disabled = errors > 0
      }
    }
    return disabled
  }

  return (
    <Drawer
      key='resources-capacity-drawer'
      title={[
        t(`resources.capacity.modal.title.${formAction}`),
        <b>
          {' '}
          {initialValues.first_name} {initialValues.last_name}
        </b>
      ]}
      width={400}
      onClose={handleShowDrawer}
      visible={showDrawerCapacity}
      className='capacity-by-resource'
    >
      {initialValues && (
        <>
          <Steps size='small' current={state}>
            {steps.map((item, ix) => (
              <Step key={ix} title={item.title} />
            ))}
          </Steps>
          <div className='steps-content'>{steps[state].content}</div>
          <div className='steps-action'>
            {state < steps.length - 1 && (
              <Button type='primary' onClick={() => setState(state + 1)} disabled={isNextDisabled(state)}>
                {t('resources.capacity.modal.next')}
              </Button>
            )}
            {state === steps.length - 1 && (
              <Button loading={btnLoading} type='primary' onClick={() => done()} disabled={isNextDisabled(state)}>
                {t('resources.capacity.modal.done')}
              </Button>
            )}
            {state > 0 && (
              <Button style={{ marginLeft: 8 }} onClick={() => setState(state - 1)}>
                {t('resources.capacity.modal.previous')}
              </Button>
            )}
          </div>
        </>
      )}
    </Drawer>
  )
}

export default Capacity
